import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Edit, UserX, CheckCircle, XCircle, Search, User, MapPin, Shield, Briefcase, Phone as PhoneIcon, Heart, ChevronLeft, ChevronRight, UserCheck, Archive, Trash2, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AtelierAccessCard } from '@/components/admin/AtelierAccessCard';

const PERMIT_TYPES = [
  { value: 'citoyen_ch', label: 'Citoyen suisse' },
  { value: 'C', label: 'Permis C - Établissement' },
  { value: 'B', label: 'Permis B - Séjour' },
  { value: 'L', label: 'Permis L - Courte durée' },
  { value: 'G', label: 'Permis G - Frontalier' },
  { value: 'F', label: 'Permis F - Admission provisoire' },
  { value: 'N', label: 'Permis N - Requérant d\'asile' },
  { value: 'S', label: 'Permis S - Protection provisoire' },
  { value: 'autre', label: 'Autre' },
];

const NATIONALITIES = [
  'Suisse', 'Française', 'Allemande', 'Italienne', 'Portugaise', 'Espagnole',
  'Turque', 'Kosovare', 'Serbe', 'Croate', 'Bosniaque', 'Macédonienne',
  'Albanaise', 'Roumaine', 'Polonaise', 'Britannique', 'Américaine',
  'Brésilienne', 'Érythréenne', 'Somalienne', 'Afghane', 'Syrienne',
  'Congolaise', 'Camerounaise', 'Ivoirienne', 'Marocaine', 'Tunisienne',
  'Algérienne', 'Sri-lankaise', 'Thaïlandaise', 'Indienne', 'Chinoise',
  'Russe', 'Ukrainienne', 'Autre',
];

const CANTONS = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU',
  'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR',
  'VD', 'VS', 'ZG', 'ZH',
];

const CONTRACT_TYPES = [
  { value: 'cdi', label: 'CDI (Contrat durée indéterminée)' },
  { value: 'cdd', label: 'CDD (Contrat durée déterminée)' },
  { value: 'temporaire', label: 'Temporaire' },
  { value: 'apprenti', label: 'Apprentissage' },
  { value: 'stagiaire', label: 'Stage' },
];

const MARITAL_STATUSES = [
  { value: 'celibataire', label: 'Célibataire' },
  { value: 'marie', label: 'Marié(e)' },
  { value: 'divorce', label: 'Divorcé(e)' },
  { value: 'veuf', label: 'Veuf/Veuve' },
  { value: 'partenariat', label: 'Partenariat enregistré' },
];

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', pin_code: '', password: '',
  role: 'employe', date_of_birth: '', nationality: '', marital_status: '',
  phone: '', address_street: '', address_postal_code: '', address_city: '',
  address_canton: '', avs_number: '', permit_type: '', permit_other: '',
  permit_expiry: '', date_of_hire: '', position: '', department: '',
  contract_type: '', occupation_rate: 100, weekly_hours: 42.5,
  daily_hours: 8.5, leave_balance: 20, emergency_contact_name: '',
  emergency_contact_phone: '', bank_iban: '', notes: '',
};

const ROLE_LABELS = {
  admin: 'Administrateur', reception: 'Réception', vente: 'Vente',
  reception_vente: 'Réception + Vente', rh: 'Ressources humaines',
  mecanicien: 'Mécanicien', carrossier: 'Carrossier',
  apprenti: 'Apprenti mécanique', stagiaire: 'Stagiaire', employe: 'Employé',
};

const TABS = [
  { id: 'identite', label: 'Identité', icon: User },
  { id: 'coordonnees', label: 'Coordonnées', icon: MapPin },
  { id: 'administratif', label: 'Administratif', icon: Shield },
  { id: 'emploi', label: 'Emploi', icon: Briefcase },
  { id: 'urgence', label: 'Urgence & Banque', icon: Heart },
];

const AdminEmployees = ({
 auth, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState('identite');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewTab, setViewTab] = useState('active');

  useEffect(() => { loadEmployees(); }, []);

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const set = (key, val) => setFormData(prev => {
    const updated = { ...prev, [key]: val };
    // Auto-fill position when role changes and position is empty
    if (key === 'role' && !prev.position) {
      updated.position = ROLE_LABELS[val] || '';
    }
    return updated;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Only allow submission from the last tab - pressing Enter on other tabs advances to next
    if (activeTab !== 'urgence') {
      goNext();
      return;
    }
    if (!formData.first_name || !formData.last_name) {
      toast.error('Prenom et nom sont obligatoires');
      return;
    }
    const needsPanel = ['admin', 'reception', 'vente', 'reception_vente', 'rh'].includes(formData.role);
    if (needsPanel && !formData.email) {
      toast.error('Email obligatoire pour ce role');
      setActiveTab('identite');
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Format d\'email invalide');
      setActiveTab('identite');
      return;
    }
    if (!editingEmployee && (!formData.pin_code || formData.pin_code.length !== 4)) {
      toast.error('Code PIN de 4 chiffres obligatoire');
      setActiveTab('identite');
      return;
    }
    if (needsPanel && !editingEmployee && !formData.password) {
      toast.error('Mot de passe requis pour ce role');
      setActiveTab('identite');
      return;
    }

    try {
      if (editingEmployee) {
        const updateData = { ...formData };
        delete updateData.password;
        delete updateData.pin_code;
        updateData.daily_hours = parseFloat(updateData.weekly_hours) / 5;
        updateData.weekly_hours = parseFloat(updateData.weekly_hours);
        updateData.leave_balance = parseInt(updateData.leave_balance);
        updateData.occupation_rate = parseFloat(updateData.occupation_rate);
        if (!updateData.email) updateData.email = null;
        if (formData.pin_code && formData.pin_code !== '****' && formData.pin_code.length === 4) updateData.pin_code = formData.pin_code;
        if (formData.password) updateData.password = formData.password;
        await axios.put(`${API}/employees/${editingEmployee.id}`, updateData);
        toast.success('Employe modifie');
      } else {
        const createData = { ...formData };
        createData.daily_hours = parseFloat(createData.weekly_hours) / 5;
        createData.weekly_hours = parseFloat(createData.weekly_hours);
        createData.leave_balance = parseInt(createData.leave_balance);
        createData.occupation_rate = parseFloat(createData.occupation_rate);
        if (!createData.email) createData.email = null;
        if (!createData.password) delete createData.password;
        await axios.post(`${API}/employees`, createData);
        toast.success('Employe cree');
      }
      setShowDialog(false);
      resetForm();
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (emp) => {
    setEditingEmployee(emp);
    const role = emp.role || 'employe';
    const position = emp.position || '';
    setFormData({
      first_name: emp.first_name || '', last_name: emp.last_name || '',
      email: emp.email || '', pin_code: emp.pin_code ? '****' : '', password: '',
      role,
      date_of_birth: emp.date_of_birth || '', nationality: emp.nationality || '',
      marital_status: emp.marital_status || '', phone: emp.phone || '',
      address_street: emp.address_street || '', address_postal_code: emp.address_postal_code || '',
      address_city: emp.address_city || '', address_canton: emp.address_canton || '',
      avs_number: emp.avs_number || '', permit_type: emp.permit_type || '',
      permit_other: emp.permit_other || '', permit_expiry: emp.permit_expiry || '',
      date_of_hire: emp.date_of_hire || '',
      position: position || ROLE_LABELS[role] || '',
      department: emp.department || '', contract_type: emp.contract_type || '',
      occupation_rate: emp.occupation_rate ?? 100, weekly_hours: emp.weekly_hours || 42.5,
      daily_hours: emp.daily_hours || 8.5, leave_balance: emp.leave_balance ?? 20,
      emergency_contact_name: emp.emergency_contact_name || '',
      emergency_contact_phone: emp.emergency_contact_phone || '',
      bank_iban: emp.bank_iban || '', notes: emp.notes || '',
    });
    setActiveTab('identite');
    setShowDialog(true);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Désactiver cet employé ? Son historique sera conservé.')) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Employé désactivé');
      loadEmployees();
    } catch { toast.error('Erreur'); }
  };

  const handleReactivate = async (id) => {
    try {
      await axios.put(`${API}/employees/${id}`, { is_active: true });
      toast.success('Employe reactive');
      loadEmployees();
    } catch { toast.error('Erreur'); }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handlePermanentDelete = async (emp) => {
    if (emp.email === 'admin@vortekio.ch') { toast.error('Impossible de supprimer le compte admin'); return; }
    if (confirmDeleteId !== emp.id) {
      setConfirmDeleteId(emp.id);
      toast('Cliquez encore pour confirmer la suppression', { duration: 3000 });
      setTimeout(() => setConfirmDeleteId(null), 3000);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await axios.delete(`${API}/employees/${emp.id}/permanent`);
      toast.success(`${emp.first_name} ${emp.last_name} supprime`);
      loadEmployees();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de suppression'); }
  };

  const handlePurgeFormer = async () => {
    try {
      const res = await axios.delete(`${API}/employees-purge/former`);
      toast.success(res.data.message);
      loadEmployees();
    } catch { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({ ...EMPTY_FORM });
    setActiveTab('identite');
  };

  const filtered = employees.filter(emp => {
    const isActive = emp.is_active !== false;
    if (viewTab === 'active' && !isActive) return false;
    if (viewTab === 'former' && isActive) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(q) ||
      (emp.email || '').toLowerCase().includes(q) ||
      (emp.phone || '').toLowerCase().includes(q) ||
      (emp.position || '').toLowerCase().includes(q);
  });

  const activeCount = employees.filter(e => e.is_active !== false).length;
  const formerCount = employees.filter(e => e.is_active === false).length;

  const needsPanel = ['admin', 'reception', 'vente', 'reception_vente', 'rh'].includes(formData.role);

  const tabIdx = TABS.findIndex(t => t.id === activeTab);
  const goNext = () => { if (tabIdx < TABS.length - 1) setActiveTab(TABS[tabIdx + 1].id); };
  const goPrev = () => { if (tabIdx > 0) setActiveTab(TABS[tabIdx - 1].id); };

  const renderField = (label, key, type = 'text', props = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <Input type={type} value={formData[key] || ''} onChange={e => set(key, e.target.value)}
        className="h-9 text-sm" data-testid={`field-${key}`} {...props} />
    </div>
  );

  const renderSelect = (label, key, options, placeholder = 'Choisir...') => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <Select value={formData[key] || ''} onValueChange={v => set(key, v)}>
        <SelectTrigger className="h-9 text-sm" data-testid={`field-${key}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
              {typeof opt === 'string' ? opt : opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <AtelierAccessCard />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="employees-title">
              {t('employees.title')}
            </h1>
            <p className="text-base text-slate-600 mt-1">{filtered.length} employé(s) affiché(s)</p>
          </div>
          <Dialog open={showDialog} onOpenChange={open => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-md" data-testid="add-employee-button">
                <UserPlus className="mr-2 w-5 h-5" /> Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>{editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}</DialogTitle>
              </DialogHeader>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-slate-200 overflow-x-auto pb-0" data-testid="form-tabs">
                {TABS.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`} data-testid={`tab-${tab.id}`}>
                      <Icon className="w-3.5 h-3.5" />{tab.label}
                    </button>
                  );
                })}
              </div>

              <form onSubmit={handleSubmit} onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName === 'INPUT' && activeTab !== 'urgence') {
                  e.preventDefault();
                  goNext();
                }
              }} className="flex-1 overflow-y-auto px-1 py-3 space-y-4">
                {/* TAB: Identité */}
                {activeTab === 'identite' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {renderField('Prénom *', 'first_name')}
                      {renderField('Nom *', 'last_name')}
                    </div>
                    {renderField(needsPanel ? 'Email *' : 'Email (optionnel)', 'email', 'email')}
                    <div className="grid grid-cols-2 gap-3">
                      {renderField('Date de naissance', 'date_of_birth', 'date')}
                      {renderSelect('État civil', 'marital_status', MARITAL_STATUSES)}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {renderSelect('Nationalité', 'nationality', NATIONALITIES)}
                      {renderField('Téléphone', 'phone', 'tel')}
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Acces au systeme</p>
                      {renderSelect('Role *', 'role', [
                        { value: 'mecanicien', label: 'Mecanicien' },
                        { value: 'apprenti', label: 'Apprenti mecanique' },
                        { value: 'stagiaire', label: 'Stagiaire' },
                        { value: 'carrossier', label: 'Carrossier' },
                        { value: 'employe', label: 'Employe' },
                        { value: 'reception', label: 'Reception' },
                        { value: 'vente', label: 'Vente' },
                        { value: 'reception_vente', label: 'Reception + Vente' },
                        { value: 'rh', label: 'Ressources humaines' },
                        { value: 'admin', label: 'Administrateur' },
                      ])}

                      {/* PIN code - obligatoire pour tous (pointage) */}
                      <div className="mt-3 space-y-1.5">
                        <Label className="text-xs font-medium text-slate-600">
                          Code PIN (4 chiffres) {!editingEmployee && '*'}
                          {editingEmployee && formData.pin_code === '****' && (
                            <span className="ml-2 text-emerald-600 font-normal">PIN configure</span>
                          )}
                        </Label>
                        <Input type={formData.pin_code === '****' ? 'text' : 'password'} inputMode="numeric" pattern="[0-9]*" maxLength={4}
                          value={formData.pin_code} 
                          onChange={e => set('pin_code', e.target.value.replace(/\D/g, ''))}
                          onFocus={() => { if (formData.pin_code === '****') set('pin_code', ''); }}
                          placeholder={editingEmployee ? 'Cliquer pour modifier' : '1234'}
                          className="h-9 text-sm" data-testid="field-pin_code" />
                        <p className="text-xs text-slate-400">Connexion pointage + dashboard (page /scan)</p>
                      </div>

                      {/* Mot de passe - uniquement pour les rôles panel admin */}
                      {needsPanel && (
                        <div className="mt-3 space-y-1.5">
                          <Label className="text-xs font-medium text-slate-600">
                            Mot de passe panel admin {!editingEmployee && '*'}
                          </Label>
                          <Input type="password" value={formData.password}
                            onChange={e => set('password', e.target.value)}
                            placeholder={editingEmployee ? 'Laisser vide = inchange' : 'Mot de passe'}
                            className="h-9 text-sm" data-testid="field-password" />
                          <p className="text-xs text-slate-400">
                            Panel admin avec {formData.email || 'email'} + mot de passe
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* TAB: Coordonnées */}
                {activeTab === 'coordonnees' && (
                  <div className="space-y-4">
                    {renderField('Rue et numéro', 'address_street')}
                    <div className="grid grid-cols-3 gap-3">
                      {renderField('NPA', 'address_postal_code')}
                      {renderField('Ville', 'address_city')}
                      {renderSelect('Canton', 'address_canton', CANTONS.map(c => ({ value: c, label: c })))}
                    </div>
                    {renderField('Téléphone', 'phone', 'tel')}
                    {renderField('Email', 'email', 'email')}
                  </div>
                )}

                {/* TAB: Administratif */}
                {activeTab === 'administratif' && (
                  <div className="space-y-4">
                    {renderField('Numéro AVS', 'avs_number', 'text', { placeholder: '756.1234.5678.97' })}
                    <div className="grid grid-cols-2 gap-3">
                      {renderSelect('Permis de séjour', 'permit_type', PERMIT_TYPES)}
                      {formData.permit_type === 'autre' && renderField('Préciser', 'permit_other')}
                      {formData.permit_type && formData.permit_type !== 'citoyen_ch' &&
                        renderField('Date d\'expiration', 'permit_expiry', 'date')}
                    </div>
                    {renderSelect('Nationalité', 'nationality', NATIONALITIES)}
                  </div>
                )}

                {/* TAB: Emploi */}
                {activeTab === 'emploi' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {renderField('Date d\'entrée en service', 'date_of_hire', 'date')}
                      {renderSelect('Type de contrat', 'contract_type', CONTRACT_TYPES)}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {renderField('Poste / Fonction', 'position')}
                      {renderField('Département', 'department')}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {renderField('Taux d\'occupation (%)', 'occupation_rate', 'number', { min: 0, max: 100, step: 5 })}
                      {renderField('Heures/semaine', 'weekly_hours', 'number', { step: 0.5 })}
                      {renderField('Jours congés/an', 'leave_balance', 'number')}
                    </div>
                  </div>
                )}

                {/* TAB: Urgence & Banque */}
                {activeTab === 'urgence' && (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-slate-700">Contact d'urgence</p>
                    <div className="grid grid-cols-2 gap-3">
                      {renderField('Nom du contact', 'emergency_contact_name')}
                      {renderField('Téléphone d\'urgence', 'emergency_contact_phone', 'tel')}
                    </div>
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Coordonnées bancaires</p>
                      {renderField('IBAN', 'bank_iban', 'text', { placeholder: 'CH00 0000 0000 0000 0000 0' })}
                    </div>
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Notes</p>
                      <Textarea value={formData.notes} onChange={e => set('notes', e.target.value)}
                        placeholder="Notes internes..." rows={3} className="text-sm" data-testid="field-notes" />
                    </div>
                  </div>
                )}

                {/* Navigation + Submit */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <Button type="button" variant="outline" size="sm" onClick={goPrev}
                    disabled={tabIdx === 0} className="text-xs">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
                  </Button>

                  {tabIdx === TABS.length - 1 ? (
                    <Button type="submit" className="btn-primary text-sm" data-testid="save-employee-button">
                      {editingEmployee ? 'Modifier' : 'Créer l\'employé'}
                    </Button>
                  ) : (
                    <Button type="button" size="sm" onClick={goNext} className="text-xs bg-blue-600 hover:bg-blue-700 text-white">
                      Suivant <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs: Actifs / Anciens */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="employee-view-tabs">
          <button
            onClick={() => setViewTab('active')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              viewTab === 'active' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-active-employees"
          >
            <UserCheck className="w-4 h-4" />
            Employes actifs
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{activeCount}</span>
          </button>
          <button
            onClick={() => setViewTab('former')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              viewTab === 'former' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-former-employees"
          >
            <Archive className="w-4 h-4" />
            Anciens employes
            {formerCount > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{formerCount}</span>}
          </button>
        </div>

        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Rechercher par nom, email, telephone, poste..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-10"
              data-testid="search-employees" />
          </div>
        </div>

        {/* Purge all former employees button */}
        {viewTab === 'former' && formerCount > 0 && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={handlePurgeFormer} data-testid="purge-former-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer tous les anciens ({formerCount})
            </Button>
          </div>
        )}

        {/* Employee Table */}
        {loading ? (
          <div className="text-center py-12"><p className="text-slate-500">Chargement...</p></div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center rounded-xl"><p className="text-slate-500">Aucun employé trouvé</p></Card>
        ) : (
          <Card className="p-0 rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {['Nom complet', 'Poste', 'Téléphone', 'Permis', 'Taux', 'H/sem', 'Congés', 'Statut', 'Actions'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(emp => (
                    <tr key={emp.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!emp.is_active ? 'opacity-50' : ''}`}
                      data-testid={`employee-row-${emp.id}`}>
                      <td className="py-3 px-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-900">{emp.first_name} {emp.last_name}</p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                              emp.role === 'admin' ? 'bg-red-100 text-red-700' :
                              emp.role === 'reception' ? 'bg-sky-100 text-sky-700' :
                              emp.role === 'vente' ? 'bg-emerald-100 text-emerald-700' :
                              emp.role === 'reception_vente' ? 'bg-violet-100 text-violet-700' :
                              emp.role === 'mecanicien' || emp.role === 'carrossier' ? 'bg-amber-100 text-amber-700' :
                              emp.role === 'apprenti' || emp.role === 'stagiaire' ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-100 text-slate-600'
                            }`} data-testid={`role-badge-${emp.id}`}>{
                              { admin: 'Admin', reception: 'Reception', vente: 'Vente', reception_vente: 'Rec+Vente',
                                mecanicien: 'Mecano', carrossier: 'Carrossier', apprenti: 'Apprenti',
                                stagiaire: 'Stagiaire', rh: 'RH', employe: 'Employe' }[emp.role] || emp.role
                            }</span>
                          </div>
                          <p className="text-xs text-slate-500">{emp.email || 'Pas d\'email'}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {emp.position || ROLE_LABELS[emp.role] || emp.role || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{emp.phone || '-'}</td>
                      <td className="py-3 px-4">
                        {emp.permit_type ? (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700">
                            {emp.permit_type === 'citoyen_ch' ? 'CH' : emp.permit_type}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-center font-mono text-slate-700">
                        {`${emp.occupation_rate ?? 100}%`}
                      </td>
                      <td className="py-3 px-4 text-sm text-center font-mono text-slate-700">
                        {emp.weekly_hours || 42.5}
                      </td>
                      <td className="py-3 px-4 text-sm text-center font-mono text-slate-700">
                        {emp.leave_balance ?? 20}
                      </td>
                      <td className="py-3 px-4">
                        {emp.is_active ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                            <CheckCircle className="w-4 h-4" /> Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium">
                            <XCircle className="w-4 h-4" /> Inactif
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right space-x-1">
                        <Button size="sm" variant="outline" className="text-emerald-600 hover:bg-emerald-50"
                          onClick={() => navigate(`/admin/employees/${emp.id}/hr`)}
                          data-testid={`hr-dashboard-${emp.id}`}
                          title="Tableau de bord RH">
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(emp)}
                          data-testid={`edit-employee-${emp.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {emp.is_active ? (
                          <>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeactivate(emp.id)}
                              data-testid={`deactivate-employee-${emp.id}`}>
                              <UserX className="w-4 h-4" />
                            </Button>
                            {emp.email !== 'admin@vortekio.ch' && (
                              <Button size="sm" variant={confirmDeleteId === emp.id ? "destructive" : "outline"}
                                className={confirmDeleteId === emp.id ? "animate-pulse" : "text-red-700 hover:bg-red-100 border-red-300"}
                                onClick={() => handlePermanentDelete(emp)}
                                data-testid={`permanent-delete-${emp.id}`}>
                                <Trash2 className="w-4 h-4" />
                                {confirmDeleteId === emp.id && <span className="ml-1 text-xs">Confirmer</span>}
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleReactivate(emp.id)}
                              data-testid={`reactivate-employee-${emp.id}`}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant={confirmDeleteId === emp.id ? "destructive" : "outline"}
                              className={confirmDeleteId === emp.id ? "animate-pulse" : "text-red-600 hover:bg-red-50"}
                              onClick={() => handlePermanentDelete(emp)}
                              data-testid={`permanent-delete-${emp.id}`}>
                              <Trash2 className="w-4 h-4" />
                              {confirmDeleteId === emp.id && <span className="ml-1 text-xs">Confirmer</span>}
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEmployees;
